import React from 'react';
import {
    StyledFormControl,
    StyledInputLabel,
    StyledTextInput,
    StyledFormHelperText,
    AdornmentWrapper,
} from './styled';
import { ITextInputProps } from './types';

/**
 * Componente TextInput customizado baseado no OutlinedInput do Material-UI
 * 
 * @example
 * <TextInput
 *   label="Email"
 *   placeholder="Digite seu email"
 *   startAdornment={<EmailIcon />}
 *   error={!!errors.email}
 *   helperText={errors.email?.message}
 * />
 * 
 * @example
 * <TextInput
 *   label="Senha"
 *   type="password"
 *   endAdornment={
 *     <IconButton edge="end">
 *       <VisibilityIcon />
 *     </IconButton>
 *   }
 * />
 */
const TextInput: React.FC<ITextInputProps> = ({
    // Nossas props customizadas
    startAdornment,
    endAdornment,
    label,
    helperText,
    error = false,
    variant = 'outlined',
    size = 'medium',

    // Props do Material-UI
    id,
    required,
    disabled,
    fullWidth = true,
    placeholder,
    type = 'text',
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,

    // Outras props
    className,
    sx,
    ...props
}) => {
    // Gera um ID único se não for fornecido
    const inputId = id || `text-input`;
    const hasStartAdornment = !!startAdornment;
    const hasEndAdornment = !!endAdornment;

    // Renderiza apenas o Input se não tiver label ou helper text
    if (!label && !helperText) {
        return (
            <StyledTextInput
                id={inputId}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={placeholder}
                type={type}
                required={required}
                disabled={disabled}
                fullWidth={fullWidth}
                error={error}
                size={size}
                className={className}
                sx={sx}
                hasStartAdornment={hasStartAdornment}
                hasEndAdornment={hasEndAdornment}
                startAdornment={
                    startAdornment && (
                        <AdornmentWrapper className={error ? 'Mui-error' : ''}>
                            {startAdornment}
                        </AdornmentWrapper>
                    )
                }
                endAdornment={
                    endAdornment && (
                        <AdornmentWrapper className={error ? 'Mui-error' : ''}>
                            {endAdornment}
                        </AdornmentWrapper>
                    )
                }
                {...props}
            />
        );
    }

    // Renderiza com FormControl completo (label + helper text)
    return (
        <StyledFormControl
            variant={variant}
            fullWidth={fullWidth}
            error={error}
            disabled={disabled}
            className={className}
            sx={sx}
        >
            {label && (
                <StyledInputLabel htmlFor={inputId} required={required}>
                    {label}
                </StyledInputLabel>
            )}

            <StyledTextInput
                id={inputId}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={placeholder}
                type={type}
                required={required}
                disabled={disabled}
                label={label ? label : undefined}
                size={size}
                hasStartAdornment={hasStartAdornment}
                hasEndAdornment={hasEndAdornment}
                startAdornment={
                    startAdornment && (
                        <AdornmentWrapper className={error ? 'Mui-error' : ''}>
                            {startAdornment}
                        </AdornmentWrapper>
                    )
                }
                endAdornment={
                    endAdornment && (
                        <AdornmentWrapper className={error ? 'Mui-error' : ''}>
                            {endAdornment}
                        </AdornmentWrapper>
                    )
                }
                {...props}
            />

            {helperText && (
                <StyledFormHelperText error={error}>
                    {helperText}
                </StyledFormHelperText>
            )}
        </StyledFormControl>
    );
};

export { TextInput };